import { useDraggable } from '@dnd-kit/core';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { format, parseISO, differenceInDays, differenceInWeeks } from 'date-fns';
import { Clock, Calendar as CalendarIcon, AlertTriangle, Package, Trash2 } from 'lucide-react';
import { deleteWorkOrder } from '@/lib/api';
import { useState } from 'react';

export default function WorkOrderCard({ workOrder, isDraggable = true, onDelete, user }) {
  const [isDeleting, setIsDeleting] = useState(false);
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: workOrder.id,
    disabled: !isDraggable,
  });

  // Handle null/undefined dates safely
  const dueDate = workOrder.due_date ? parseISO(workOrder.due_date) : null;
  const daysUntilDue = dueDate ? differenceInDays(dueDate, new Date()) : 999;
  const weeksUntilDue = dueDate ? differenceInWeeks(dueDate, new Date()) : 999;
  const isUrgent = daysUntilDue <= 3;
  const isWarning = daysUntilDue > 3 && daysUntilDue <= 7;

  const handleDelete = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    console.log('Delete clicked for WO:', workOrder.wo_number, 'ID:', workOrder.id);
    
    if (typeof window !== 'undefined' && !window.confirm(`Are you sure you want to delete work order ${workOrder.wo_number}?`)) {
      console.log('Delete cancelled by user');
      return;
    }
    
    console.log('Delete confirmed, proceeding...');
    setIsDeleting(true);
    
    try {
      console.log('Calling deleteWorkOrder API...');
      const result = await deleteWorkOrder(workOrder.id);
      console.log('Delete API result:', result);
      
      if (onDelete) {
        console.log('Calling onDelete callback');
        onDelete(workOrder.id);
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert(`Failed to delete work order: ${error.message}`);
      setIsDeleting(false);
    }
  };

  // Buildable status logic
  const quantity = workOrder.quantity || 0;
  const buildable = workOrder.buildable || 0;
  const showBuildableWarning = weeksUntilDue < 2;
  
  let buildableStatus = 'green'; // Fully buildable
  let buildableText = 'Buildable';
  
  if (buildable === 0) {
    buildableStatus = 'red';
    buildableText = 'Not Buildable';
  } else if (buildable < quantity) {
    buildableStatus = 'yellow';
    buildableText = 'Partially Buildable';
  }

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`work-order-card p-3 border-l-4 ${
        isUrgent
          ? 'border-l-red-500'
          : isWarning
          ? 'border-l-amber-500'
          : 'border-l-emerald-500'
      } ${isDragging ? 'opacity-50' : ''}`}
      data-testid="work-order-card"
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <Badge
            variant="secondary"
            className="font-mono font-semibold text-xs"
            data-testid="sequence-badge"
          >
            #{workOrder.sequence}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Badge
            className={`text-xs ${
              isUrgent
                ? 'bg-red-100 text-red-800'
                : isWarning
                ? 'bg-amber-100 text-amber-800'
                : 'bg-emerald-100 text-emerald-800'
            }`}
          >
            {daysUntilDue}d
          </Badge>
          {user?.role === 'Administrator' && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 hover:bg-red-100 hover:text-red-600"
              onClick={handleDelete}
              disabled={isDeleting}
              title="Delete work order"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-1">
        <h4 className="font-bold text-sm leading-tight" data-testid="wo-number">
          {workOrder.wo_number}
        </h4>
        {workOrder.assembly_item && (
          <p className="text-xs font-medium text-sky-600 truncate" data-testid="assembly-item">
            {workOrder.assembly_item}
          </p>
        )}
        <p className="text-xs text-muted-foreground truncate" data-testid="customer-name">
          {workOrder.customer}
        </p>
        
        {/* Additional NetSuite Fields */}
        <div className="text-[10px] text-muted-foreground space-y-0.5 mt-1">
          {workOrder.tag && (
            <p className="truncate"><span className="font-semibold">Tag:</span> {workOrder.tag}</p>
          )}
          {workOrder.serial_number && (
            <p className="truncate"><span className="font-semibold">SN:</span> {workOrder.serial_number}</p>
          )}
          {workOrder.project && (
            <p className="truncate"><span className="font-semibold">Project:</span> {workOrder.project}</p>
          )}
          {workOrder.cust_po && (
            <p className="truncate"><span className="font-semibold">CustPO:</span> {workOrder.cust_po}</p>
          )}
        </div>
      </div>

      <div className="mt-3 space-y-1">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <CalendarIcon className="h-3 w-3" />
          <span data-testid="due-date">
            {dueDate ? format(dueDate, 'MMM d, yyyy') : 'No due date'}
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs font-mono font-semibold">
          <Clock className="h-3 w-3" />
          <span data-testid="man-hours">{workOrder.man_hours}h</span>
        </div>
        <div className="flex items-center gap-2 text-xs font-mono font-semibold">
          <Package className="h-3 w-3" />
          <span data-testid="quantity">
            {buildable}/{quantity} units
          </span>
        </div>
      </div>

      {showBuildableWarning && (
        <div className="mt-3 pt-3 border-t border-border">
          <div
            className={`flex items-center gap-2 text-xs font-semibold ${
              buildableStatus === 'red'
                ? 'text-red-600'
                : buildableStatus === 'yellow'
                ? 'text-amber-600'
                : 'text-emerald-600'
            }`}
            data-testid="buildable-indicator"
          >
            {buildableStatus !== 'green' && <AlertTriangle className="h-3 w-3" />}
            <span>{buildableText}</span>
          </div>
        </div>
      )}
    </Card>
  );
}
